import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Configuracao base da API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api`,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      timeout: 30000, // 30 segundos
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Adicionar token de autenticacao se existir
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Log da requisicao em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data);
        }
        
        return config;
      },
      (error) => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        // Log da resposta em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
        }
        
        return response;
      },
      (error) => {
        console.error('[API Response Error]', error);
        
        // Tratar erros comuns
        if (error.response?.status === 401) {
          // Token expirado ou invalido
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        } else if (error.response?.status === 403) {
          // Senha ainda provisoria: o servidor recusa tudo ate a troca. Pode
          // acontecer no meio da sessao, quando um administrador redefine a senha
          // de alguem que ja estava logado.
          if (error.response?.data?.codigo === 'senha_provisoria'
              && window.location.pathname !== '/trocar-senha') {
            window.location.href = '/trocar-senha';
          } else {
            console.error('Acesso negado');
          }
        } else if (error.response?.status >= 500) {
          // Erro do servidor
          console.error('Erro interno do servidor');
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Metodos HTTP genericos
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.patch(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url, config);
    return response.data;
  }

  // Metodo para upload de arquivos
  async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    };

    return this.post<T>(url, formData, config);
  }

  // Metodo para download de arquivos
  async download(url: string, filename?: string): Promise<void> {
    const response = await this.api.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Configurar token de autenticacao
  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
    this.api.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  // Remover token de autenticacao
  removeAuthToken(): void {
    localStorage.removeItem('auth_token');
    delete this.api.defaults.headers.common.Authorization;
  }

  // Verificar status da API
  async healthCheck(): Promise<any> {
    return this.get('/public/health');
  }
}

// Exportar instancia unica
export const apiService = new ApiService();
export default apiService;
